/**
 * Release Orders List Page — Issue #345.
 *
 * Uses the shared AdminDataTable + the new useReleaseOrderTransitionById hook
 * for permission-aware row actions. Release orders are laboratory-grade audit
 * records; the only non-physical removal semantic the backend exposes is the
 * `recalled` state, so 撤回 / 归档 wires to that transition (no physical delete).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { Eye, Pencil, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useReleaseOrdersList,
  useReleaseOrderTransitionById,
  type ReleaseOrderView,
} from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  canReadReleaseOrder,
  canUpdateReleaseOrder,
} from '@/lib/labPermissions';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';

type ReleaseStatus = 'pending' | 'approved' | 'rejected' | 'recalled';

/**
 * Issue #345 audit fix — mirror packages/domain/src/lab/release-state-machine.ts
 * so the row-action menu only exposes legal next states. `rejected` / `recalled`
 * are terminal; `pending` only legally transitions to `approved` / `rejected`
 * (撤回/归档 = recalled is not a legal next state for pending). Kept in-file
 * so admin-web has no build-time dependency on @jinzhuan/domain.
 */
const RELEASE_LEGAL_NEXT: Readonly<Record<ReleaseStatus, ReadonlyArray<ReleaseStatus>>> = {
  pending: ['approved', 'rejected'],
  approved: ['recalled'],
  rejected: [],
  recalled: [],
};
function canReleaseTransition(from: string, to: ReleaseStatus): boolean {
  const next = RELEASE_LEGAL_NEXT[from as ReleaseStatus];
  return next ? next.includes(to) : false;
}

const RELEASE_STATUS_OPTIONS: ReadonlyArray<{
  value: ReleaseStatus;
  label: string;
}> = [
  { value: 'pending', label: '待审批' },
  { value: 'approved', label: '已批准' },
  { value: 'rejected', label: '已拒绝' },
  { value: 'recalled', label: '已撤回' },
];

function getStatusLabel(status: string): string {
  return (
    RELEASE_STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status
  );
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-blue-100 text-blue-900',
    approved: 'bg-green-100 text-green-900',
    rejected: 'bg-red-100 text-red-900',
    recalled: 'bg-purple-100 text-purple-900',
  };
  return colors[status] ?? 'bg-gray-100 text-gray-900';
}

export default function ReleaseOrdersListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { fetch, error } = useReleaseOrdersList();
  const { transition, loading: transitioning } =
    useReleaseOrderTransitionById();

  const [orders, setOrders] = useState<ReleaseOrderView[]>([]);
  const [page, setPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    cellBatchId: '',
    status: '',
  });

  const limit = 20;
  const statusFilter = searchValues['status'] ?? '';
  const cellBatchFilter = (searchValues['cellBatchId'] ?? '').trim();

  const canRead = canReadReleaseOrder(permissions);
  const canUpdate = canUpdateReleaseOrder(permissions);

  const refresh = useCallback(async () => {
    if (!canRead) return;
    setPageLoading(true);
    try {
      const data = await fetch({
        page,
        limit,
        ...(statusFilter ? { status: statusFilter as ReleaseStatus } : {}),
        ...(cellBatchFilter ? { cellBatchId: cellBatchFilter } : {}),
      });
      if (data) setOrders(data.items || []);
    } catch {
      toast.error(`加载放行单失败：${error?.message ?? '未知错误'}`);
    } finally {
      setPageLoading(false);
    }
  }, [canRead, cellBatchFilter, error?.message, fetch, page, statusFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const columns: ReadonlyArray<AdminDataTableColumn<ReleaseOrderView>> = useMemo(
    () => [
      {
        key: 'id',
        header: '放行单 ID',
        alwaysVisible: true,
        render: (o) => <span className="font-mono text-sm">{o.id}</span>,
      },
      {
        key: 'cellBatchId',
        header: '批次 ID',
        render: (o) => <span className="font-mono text-sm">{o.cellBatchId}</span>,
      },
      {
        key: 'status',
        header: '状态',
        render: (o) => (
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(o.status)}`}
          >
            {getStatusLabel(o.status)}
          </span>
        ),
      },
      {
        key: 'version',
        header: '版本',
        defaultHidden: true,
        render: (o) => `v${o.version}`,
      },
      {
        key: 'createdAt',
        header: '创建时间',
        render: (o) => new Date(o.createdAt).toLocaleDateString('zh-CN'),
      },
    ],
    [],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<ReleaseOrderView>> =
    useMemo(
      () => [
        {
          key: 'view',
          label: '查看详情',
          icon: <Eye className="h-3.5 w-3.5" />,
          onSelect: (o) => navigate(`/lab/release/${o.id}`),
        },
        {
          key: 'edit',
          label: '更新状态',
          icon: <Pencil className="h-3.5 w-3.5" />,
          visible: () => canUpdate,
          onSelect: (o) => navigate(`/lab/release/${o.id}`),
        },
        {
          key: 'recall',
          label: '撤回（归档）',
          icon: <Undo2 className="h-3.5 w-3.5" />,
          destructive: true,
          // Issue #345 audit fix：放行状态机仅允许 approved -> recalled。
          // pending 状态的撤回应走 rejected 路径，由审核人在详情页执行；
          // 这里只对状态机允许 -> recalled 的源态显示该动作。
          visible: (o) => canUpdate && canReleaseTransition(o.status, 'recalled'),
          disabled: () => transitioning,
          onSelect: async (o) => {
            const confirmed = window.confirm(
              `将放行单「${o.id}」撤回（标记 recalled）。该操作不会物理删除任何放行历史；操作会写入审计日志，是否继续？`,
            );
            if (!confirmed) return;
            const ok = await transition(o.id, 'recalled', o.version);
            if (ok) {
              toast.success('放行单已撤回');
              await refresh();
            }
          },
        },
      ],
      [canUpdate, navigate, refresh, transition, transitioning],
    );

  if (!canRead) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看放行单的权限</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">放行单管理</h1>
        <Button type="button" variant="outline" size="sm" onClick={refresh}>
          刷新
        </Button>
      </div>

      <AdminDataTable<ReleaseOrderView>
        testIdPrefix="release-orders"
        columns={columns}
        rows={orders}
        isLoading={pageLoading}
        emptyMessage="暂无放行单数据"
        filters={{
          fields: [
            {
              kind: 'text',
              name: 'cellBatchId',
              label: '批次 ID',
              placeholder: '按批次 ID 精确搜索',
            },
            {
              kind: 'select',
              name: 'status',
              label: '状态',
              options: RELEASE_STATUS_OPTIONS.map(({ value, label }) => ({
                value,
                label,
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => {
            setSearchValues(next);
            setPage(1);
          },
          onReset: () => {
            setSearchValues({ cellBatchId: '', status: '' });
            setPage(1);
          },
        }}
        pagination={{
          page,
          pageSize: limit,
          total:
            orders.length === limit
              ? page * limit + 1
              : (page - 1) * limit + orders.length,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />
    </div>
  );
}
